import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useWaitForTransactionReceipt, useWriteContract } from 'wagmi'
import { decodeEventLog, maxUint256, type Address, type Hash } from 'viem'
import { ADDRESSES } from '../config/faucet.config'
import { erc20Abi, erc1155Abi, openFaucetAbi, sipRouterAbi } from '../abi'
import { isUserRejection, readableError } from '../lib/format'

export type Step =
  | 'idle'
  | 'approving-cbbtc'
  | 'approving-leaves'
  | 'approving-cbbtc-sip'
  | 'sipping'
  | 'drawing'
  | 'confirming'
  | 'success'
  | 'sip-success'
  | 'failed'

/**
 * What the participant asked for, captured AT SUBMISSION.
 *
 * WHY A SNAPSHOT EXISTS. A successful draw empties exactly the balances the
 * form's eligibility checks read. Refetching immediately after confirmation is
 * correct — the page must not show stale balances — but if the form is still on
 * screen, the freshly-emptied wallet re-renders as "not enough taboshi1: you
 * hold 0", which makes a SUCCESSFUL transaction read as a failure. So the terms
 * of the draw are frozen here when the transaction is signed, and the success
 * screen renders from this record while the live reads move on underneath it.
 *
 * `maxCbBTC` doubles as the exact amount paid: the price staircase never
 * decreases, so the cost at mining time is >= the quote, and `draw` reverts
 * with `PriceExceedsMax` the moment it exceeds `maxCbBTC` (which IS the quote).
 * A success therefore paid exactly the quoted figure — not "up to" it.
 */
export interface DrawIntent {
  quantity: number
  enhanced: boolean
  /** The fresh quote, signed as maxCbBTC. On success, exactly what was paid. */
  maxCbBTC: bigint
  /** Legacy leaves this draw retires. Zero for a standard draw. */
  leaves: bigint
  /** SEED this draw mints. */
  seeds: bigint
}

export interface CompletedDraw extends DrawIntent {
  hash: Hash
}

/**
 * The settled sip, for its own sticky success screen. `sats` and `seed` are the
 * ACTUAL figures decoded from the transaction's SipTaken event — the live oracle
 * and quote can move between preview and mining (inside the signed bounds), so
 * the receipt, not the preview, is the truth the screen shows.
 */
export interface CompletedSip {
  sats: bigint
  seed: bigint
  hash: Hash
}

export interface DrawFlow {
  step: Step
  hash?: Hash
  /** The confirmed draw, present ONLY after a success receipt. Cleared by reset(). */
  completed: CompletedDraw | null
  /** The confirmed sip, present ONLY after a success receipt. Cleared by reset(). */
  completedSip: CompletedSip | null
  /** 'confirmed' only once a success receipt is in hand; 'confirming' before. */
  receiptLabel: 'confirming' | 'confirmed'
  error: string | null
  clearError: () => void
  /** True while any transaction is in flight. */
  busy: boolean
  approveCbBTC: () => Promise<void>
  approveLeaves: () => Promise<void>
  draw: (intent: DrawIntent) => Promise<void>
  /** The dormant Sip route: approve the ROUTER as a separate spender, then a sip —
   *  one immediate transaction, bounded by maxCbBTC and minSeedOut. */
  approveCbBTCForRouter: () => Promise<void>
  sip: (maxCbBTC: bigint, minSeedOut: bigint) => Promise<void>
  /** Leaves the success/failure screen and returns to the normal form. */
  reset: () => void
}

/**
 * The three transactions a participant can send, and nothing else.
 *
 * SETTLEMENT IS DRIVEN BY THE RECEIPT, NOT BY SUBMISSION. A submitted hash
 * proves nothing; the transaction can still revert. So:
 *
 *   - `onConfirmed` (the balance/state refetch) fires only once a receipt is in
 *     hand — never at submission;
 *   - a DRAW receipt with status 'success' moves to the 'success' step, carrying
 *     the snapshot taken at signing;
 *   - a DRAW receipt with status 'reverted' moves to 'failed', and no SEED
 *     figure is ever attached to it;
 *   - an approval receipt returns quietly to 'idle' so the form can offer the
 *     next step.
 *
 * The 'success'/'failed' steps are STICKY: nothing but the user's own reset()
 * leaves them, which is what stops a post-draw refetch from replacing the
 * outcome with an eligibility warning about the now-spent balances.
 */
export function useDrawFlow(onConfirmed?: () => void): DrawFlow {
  const [step, setStep] = useState<Step>('idle')
  const [error, setError] = useState<string | null>(null)
  const [hash, setHash] = useState<Hash | undefined>()
  const [completed, setCompleted] = useState<CompletedDraw | null>(null)
  const [completedSip, setCompletedSip] = useState<CompletedSip | null>(null)

  /** Snapshot of the in-flight draw. A ref, so the receipt effect can never
   *  observe a state update that has not committed yet. Null for approvals. */
  const pendingDraw = useRef<CompletedDraw | null>(null)
  /** The in-flight sip's signed bounds, pinned to its hash. The receipt's own
   *  SipTaken event supplies the actual figures; these are only the fallback. */
  const pendingSip = useRef<CompletedSip | null>(null)
  /** Guards the receipt effect so each hash settles exactly once, including
   *  under StrictMode's double-invoked effects. */
  const handledHash = useRef<Hash | null>(null)

  const { writeContractAsync } = useWriteContract()

  const {
    data: receipt,
    isError: receiptFailed,
    isLoading: isConfirming,
  } = useWaitForTransactionReceipt({
    hash,
    query: { enabled: Boolean(hash) },
  })

  useEffect(() => {
    if (!hash || handledHash.current === hash) return

    // The watcher itself failed (dropped or unfindable transaction). Surface it
    // rather than confirming anything — and rather than spinning forever.
    if (receiptFailed) {
      handledHash.current = hash
      setError('the transaction could not be confirmed. check the explorer before retrying.')
      setStep('idle')
      return
    }

    if (!receipt) return
    handledHash.current = hash

    const draw = pendingDraw.current
    const wasDraw = draw?.hash === hash
    const sipSnapshot = pendingSip.current
    const wasSip = sipSnapshot?.hash === hash

    if (receipt.status === 'success') {
      if (wasDraw) {
        setCompleted(draw)
        setStep('success')
      } else if (wasSip) {
        // The ACTUAL amounts come from the transaction's own SipTaken event; the
        // signed bounds are only the fallback if the log is somehow unreadable.
        let actual = { sats: sipSnapshot.sats, seed: sipSnapshot.seed }
        for (const log of receipt.logs) {
          if (log.address.toLowerCase() !== ADDRESSES.sipRouter.toLowerCase()) continue
          try {
            const decoded = decodeEventLog({ abi: sipRouterAbi, data: log.data, topics: log.topics })
            if (decoded.eventName === 'SipTaken') {
              actual = { sats: decoded.args.cbBTCPaid, seed: decoded.args.seedReceived }
              break
            }
          } catch {
            // not a SipTaken log; keep looking
          }
        }
        setCompletedSip({ ...actual, hash })
        setStep('sip-success')
      } else {
        setStep('idle')
      }
    } else {
      if (wasDraw || wasSip) {
        setStep('failed')
      } else {
        setStep('idle')
        setError('the approval was reverted on chain.')
      }
    }

    // Refetch balances and global faucet state ONLY now, with the outcome
    // already decided and (for a draw) the sticky screen already chosen.
    onConfirmed?.()
  }, [receipt, receiptFailed, hash, onConfirmed])

  const send = useCallback(
    async (nextStep: Step, fn: () => Promise<Hash>): Promise<Hash | null> => {
      setError(null)
      setStep(nextStep)
      try {
        const h = await fn()
        setHash(h)
        setStep('confirming')
        return h
      } catch (err) {
        // A rejection in the wallet is a normal outcome, not a fault to report.
        if (!isUserRejection(err)) setError(readableError(err))
        setStep('idle')
        return null
      }
    },
    [],
  )

  const approveCbBTC = useCallback(async () => {
    pendingDraw.current = null
    await send('approving-cbbtc', () =>
      writeContractAsync({
        address: ADDRESSES.cbBTC,
        abi: erc20Abi,
        functionName: 'approve',
        // Unlimited, so a returning participant does not re-approve every draw.
        // The spender is the immutable Faucet, which has no path to move cbBTC
        // anywhere but the treasury, and no rescue or sweep function at all.
        args: [ADDRESSES.faucet as Address, maxUint256],
      }),
    )
  }, [send, writeContractAsync])

  const approveLeaves = useCallback(async () => {
    pendingDraw.current = null
    await send('approving-leaves', () =>
      writeContractAsync({
        address: ADDRESSES.taboshi1,
        abi: erc1155Abi,
        functionName: 'setApprovalForAll',
        args: [ADDRESSES.faucet as Address, true],
      }),
    )
  }, [send, writeContractAsync])

  const draw = useCallback(
    async (intent: DrawIntent) => {
      const h = await send('drawing', () =>
        writeContractAsync({
          address: ADDRESSES.faucet,
          abi: openFaucetAbi,
          functionName: 'draw',
          args: [BigInt(intent.quantity), intent.enhanced, intent.maxCbBTC],
        }),
      )
      // The snapshot is pinned to the hash the wallet returned, so the receipt
      // effect can tell a draw from an approval without guessing.
      if (h) pendingDraw.current = { ...intent, hash: h }
    },
    [send, writeContractAsync],
  )

  // --- The dormant Sip route. ---

  const approveCbBTCForRouter = useCallback(async () => {
    pendingDraw.current = null
    pendingSip.current = null
    await send('approving-cbbtc-sip', () =>
      writeContractAsync({
        address: ADDRESSES.cbBTC,
        abi: erc20Abi,
        functionName: 'approve',
        // A SEPARATE spender from the faucet approval: the Router pulls the sip
        // price itself. Same unlimited-approval reasoning — the Router is
        // immutable and has no rescue or sweep surface.
        args: [ADDRESSES.sipRouter as Address, maxUint256],
      }),
    )
  }, [send, writeContractAsync])

  const sip = useCallback(
    async (maxCbBTC: bigint, minSeedOut: bigint) => {
      pendingDraw.current = null
      const h = await send('sipping', () =>
        writeContractAsync({
          address: ADDRESSES.sipRouter,
          abi: sipRouterAbi,
          functionName: 'sip',
          args: [maxCbBTC, minSeedOut],
        }),
      )
      if (h) pendingSip.current = { sats: maxCbBTC, seed: minSeedOut, hash: h }
    },
    [send, writeContractAsync],
  )

  const reset = useCallback(() => {
    setStep('idle')
    setHash(undefined)
    setError(null)
    setCompleted(null)
    setCompletedSip(null)
    pendingDraw.current = null
    pendingSip.current = null
    handledHash.current = null
  }, [])

  const busy =
    step === 'approving-cbbtc' ||
    step === 'approving-leaves' ||
    step === 'approving-cbbtc-sip' ||
    step === 'sipping' ||
    step === 'drawing' ||
    (step === 'confirming' && isConfirming)

  return useMemo(
    () => ({
      step,
      hash,
      completed,
      completedSip,
      receiptLabel: receipt?.status === 'success' ? ('confirmed' as const) : ('confirming' as const),
      error,
      clearError: () => setError(null),
      busy,
      approveCbBTC,
      approveLeaves,
      draw,
      approveCbBTCForRouter,
      sip,
      reset,
    }),
    [
      step,
      hash,
      completed,
      completedSip,
      receipt,
      error,
      busy,
      approveCbBTC,
      approveLeaves,
      draw,
      approveCbBTCForRouter,
      sip,
      reset,
    ],
  )
}
